import { forwardRef, type ImgHTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import logoOfficialUrl from '@yhelp/ui-tokens/assets/logo-yhelp-official.png?url';
import logoFullUrl from '@yhelp/ui-tokens/assets/logo-yhelp-full.svg?url';
import logoMarkUrl from '@yhelp/ui-tokens/assets/logo-yhelp-mark.svg?url';
import logoDarkUrl from '@yhelp/ui-tokens/assets/logo-yhelp-dark.svg?url';
import logoWhiteUrl from '@yhelp/ui-tokens/assets/logo-yhelp-white.svg?url';

export type LogoVariant = 'official' | 'full' | 'mark' | 'dark' | 'white';
export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';

const SRC: Record<LogoVariant, string> = {
  official: logoOfficialUrl,
  full: logoFullUrl,
  mark: logoMarkUrl,
  dark: logoDarkUrl,
  white: logoWhiteUrl,
};

const SIZE_OFFICIAL: Record<LogoSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-20 w-20',
  xl: 'h-32 w-32',
};

const SIZE: Record<LogoSize, string> = {
  sm: 'h-6 w-auto',
  md: 'h-8 w-auto',
  lg: 'h-12 w-auto',
  xl: 'h-16 w-auto',
};

export interface LogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  variant?: LogoVariant;
  size?: LogoSize;
}

export const Logo = forwardRef<HTMLImageElement, LogoProps>(function Logo(
  { variant = 'official', size = 'md', alt = 'yHelp', className, ...rest },
  ref,
) {
  const sizeClass = variant === 'official' ? SIZE_OFFICIAL[size] : SIZE[size];
  return (
    <img
      ref={ref}
      src={SRC[variant]}
      alt={alt}
      role="img"
      className={cn(
        sizeClass,
        variant === 'official' && 'rounded-xl object-cover shadow-brand',
        className,
      )}
      {...rest}
    />
  );
});
